import TransformPoints from 'components/plateviewer/well/TransformPoints';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { DropImageRequestParams } from '../drawingManager/constants';
import { fetchDropImageThunk } from 'redux/thunks/well';
import WellImage from '.';
import {
  getTunedImageForDrop,
  getTuningWindowForDropSnf,
} from 'components/commandpanel/ImageTuning/ImageTuningManager';
import { DropDetails, ImageSizeDetails } from '../constants';
import { useWellDropImageData } from 'redux/hooks/well';
import { isEqual } from 'lodash';
import ExperimentService from 'business/services/ExperimentService';
import { updateTuningValues } from 'redux/reducers/wellReducer';
import { SizeDetails } from 'components/canvas/constants';

interface Props {
  sizeDetails: SizeDetails;
  wellDetails: DropDetails;
  tpObject: TransformPoints;
  setTpObject: React.Dispatch<React.SetStateAction<TransformPoints>>;
  setPixelSize: (val) => void;
  maxImageSizeDetails?: ImageSizeDetails;
  updateWellsImagesSizeDetails?: (val: ImageSizeDetails) => void;
  wellIndex: number;
}

const wellImageSizeDetails = [];
function WellImageManager(props: Props) {
  const {
    sizeDetails,
    wellDetails,
    tpObject,
    setTpObject,
    setPixelSize,
    maxImageSizeDetails,
    updateWellsImagesSizeDetails,
  } = props;
  const { plateID, wellNum, dropNumber, cpID, regionID, imageBatchId } = wellDetails;
  const dispatch = useRMDispatch();
  const dropImageData = useWellDropImageData(
    plateID,
    wellNum,
    dropNumber,
    imageBatchId,
    cpID,
  );
  const [dropImage, setDropImage] = useState<any>(undefined);
  const [experimentTuningValues, setExperimentTuningValues] = useState([]);

  const setImageDetailsOnLoad = (img, wellImage) => {
    let widthRatio = 1;
    let heightRatio = 1;
    if (maxImageSizeDetails) {
      widthRatio = wellImage.width / maxImageSizeDetails.width;
      heightRatio = wellImage.height / maxImageSizeDetails.height;
    }
    const localTpObject = new TransformPoints(
      img.width,
      img.height,
      wellImage.width,
      wellImage.height,
      sizeDetails.canvasWidth * widthRatio,
      sizeDetails.canvasHeight * heightRatio,
    );
    localTpObject.leftMargin = (sizeDetails.canvasWidth - localTpObject.scalingWidth) / 2;
    localTpObject.topMargin =
      (sizeDetails.canvasHeight - localTpObject.scalingHeight) / 2;
    if (updateWellsImagesSizeDetails) {
      updateWellsImagesSizeDetails({
        width: wellImage.width,
        height: wellImage.height,
        wellDetails,
        scalingWidth: localTpObject.scalingWidth,
        scalingHeight: localTpObject.scalingHeight,
      });
    }
    wellImageSizeDetails.push({
      imgWidth: img.width,
      imgHeight: img.height,
      wellImageWidth: wellImage.width,
      wellImageHeight: wellImage.height,
      wellDetails,
    });
    setTpObject(localTpObject);
    setPixelSize(wellImage.pixelSize);
  };

  useMemo(() => {
    const img = new window.Image();
    img.src = dropImageData?.image?.wellImage ?? '';
    /**
     *RFR: This is a quick fix, we need to properly handle image tuning state for both drop image
     * and tuning side panel
     */
    img.onload = () => {
      const { isAutoLevelingOn, lowerLevelLimit, upperLevelLimit } =
        dropImageData?.image?.captureResultTuningValuesDto || {};
      setImageDetailsOnLoad(img, dropImageData?.image);
      const tuningWindowVM = getTuningWindowForDropSnf(
        experimentTuningValues,
        img,
        cpID,
        dropImageData?.image?.captureResultTuningValuesDto,
        false,
      );

      if (
        tuningWindowVM.lowerLevelLimit !== lowerLevelLimit &&
        tuningWindowVM.upperLevelLimit !== upperLevelLimit &&
        tuningWindowVM.isAutoLevelingOn !== isAutoLevelingOn
      ) {
        dispatch(
          updateTuningValues({
            plateId: plateID,
            wellNumber: wellNum,
            dropNumber,
            imageBatchId,
            cpId: cpID,
            lowerLimit: tuningWindowVM.lowerLevelLimit,
            upperLimit: tuningWindowVM.upperLevelLimit,
            autoLevelingOn: tuningWindowVM.isAutoLevelingOn ?? false,
          }),
        );
      }

      setDropImage(
        getTunedImageForDrop(
          img,
          tuningWindowVM.isAutoLevelingOn,
          tuningWindowVM.autoLevelingLowThreshold,
          tuningWindowVM.autoLevelingHighThreshold,
          tuningWindowVM.lowerLevelLimit,
          tuningWindowVM.upperLevelLimit,
        ),
      );
    };
  }, [
    dropImageData?.image,
    sizeDetails,
    maxImageSizeDetails?.height,
    maxImageSizeDetails?.width,
    maxImageSizeDetails?.scalingHeight,
    maxImageSizeDetails?.scalingWidth,
    experimentTuningValues,
  ]);

  useEffect(() => {
    if (wellDetails && dropImageData?.status === undefined) {
      setTpObject(null);
      const fetchDropImageRequest: DropImageRequestParams = {
        pid: plateID,
        wellNumber: wellNum,
        batchId: imageBatchId,
        dropNumber,
        cpID,
        regionID,
        bestFocusImageRequired: true,
      };
      dispatch(fetchDropImageThunk(fetchDropImageRequest));
    }
  }, [wellDetails]);

  useEffect(() => {
    const wellImageDetails = wellImageSizeDetails.find((w) =>
      isEqual(w.wellDetails, wellDetails),
    );
    if (!wellImageDetails) {
      return;
    }
    const maxImageWidth = maxImageSizeDetails ? maxImageSizeDetails.width : 1;
    const maxImageHeight = maxImageSizeDetails ? maxImageSizeDetails.height : 1;
    const widthRatio = maxImageSizeDetails
      ? wellImageDetails.wellImageWidth / maxImageWidth
      : 1;
    const heightRatio = maxImageSizeDetails
      ? wellImageDetails.wellImageHeight / maxImageHeight
      : 1;
    const localTpObject = new TransformPoints(
      wellImageDetails.imgWidth,
      wellImageDetails.imgHeight,
      wellImageDetails.wellImageWidth,
      wellImageDetails.wellImageHeight,
      sizeDetails.canvasWidth * widthRatio,
      sizeDetails.canvasHeight * heightRatio,
    );
    localTpObject.leftMargin = (sizeDetails.canvasWidth - localTpObject.scalingWidth) / 2;
    localTpObject.topMargin =
      (sizeDetails.canvasHeight - localTpObject.scalingHeight) / 2;
    setTpObject(localTpObject);
  }, [maxImageSizeDetails]);

  useEffect(() => {
    const fetchExperimentTuningValues = async () => {
      const expService = new ExperimentService();
      const experimentTuningValues = await expService.getExperimentImageTuningValues(
        wellDetails.expId,
      );
      setExperimentTuningValues(experimentTuningValues);
    };
    fetchExperimentTuningValues();
  }, [wellDetails.expId]);

  return (
    <WellImage
      sizeDetails={sizeDetails}
      tpObject={tpObject}
      image={dropImage}
      imageStatus={dropImageData?.status}
    />
  );
}

export default memo(WellImageManager);
